import type { GSplatParticleLifecycleEffectOptions } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import { GSplatParticleLifecycleEffect } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import { DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS } from '@/plugins/gsplat/effects/particleLifecycle'

export type GSplatChaoticParticleScatterEffectOptions = GSplatParticleLifecycleEffectOptions

export const GSPLAT_CHAOTIC_PARTICLE_SCATTER_DEFAULTS = {
  duration: 6,
  scatterScale: 0.05,
  particleSizeMode: 'fixed',
  particleTargetSize: 0.005,
  particleTargetSizeRatio: 0.0005,
  particleOpacity: 0.62,
  particleHighlightStrength: 1,
  particleGlowStrength: 0.8,
  particleGlowRadius: 0.35,
  particleJitterAmplitude: 0.001,
  particleJitterSpeed: 1,
  preScatterHoldDuration: 0.5,
  peakHoldDuration: 0.5,
  seed: 0,
  phaseWeights: DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS,
} as const

const chaoticParticleMotionFieldGLSL = /* glsl */ `
float particleChaoticHash(vec3 value) {
  return fract(sin(dot(value, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
}

vec3 particleChaoticDirection(vec3 originalCenter) {
  float effectScale = max(uEffectScale, 0.000001);
  vec3 local = originalCenter / effectScale;
  vec3 seedOffset = vec3(
    uParticleSeed * 0.0131,
    uParticleSeed * 0.0173,
    uParticleSeed * 0.0199
  );
  vec3 randomValue = vec3(
    particleChaoticHash(local + seedOffset + vec3(17.0, 3.0, 11.0)),
    particleChaoticHash(local.yzx + seedOffset + vec3(7.0, 29.0, 5.0)),
    particleChaoticHash(local.zxy + seedOffset + vec3(13.0, 19.0, 31.0))
  ) * 2.0 - 1.0;
  float lengthSquared = dot(randomValue, randomValue);

  if (lengthSquared < 0.000001) {
    return normalize(vec3(0.57735, -0.57735, 0.57735));
  }
  return randomValue * inversesqrt(lengthSquared);
}

vec3 particleLifecycleOffset(vec3 originalCenter, float scatterProgress) {
  float speed = mix(
    0.55,
    1.45,
    particleChaoticHash(
      originalCenter / max(uEffectScale, 0.000001) + vec3(uParticleSeed + 47.0)
    )
  );
  return particleChaoticDirection(originalCenter)
    * speed
    * scatterProgress
    * uScatterScale
    * uEffectScale;
}
`

const chaoticParticleMotionFieldWGSL = /* wgsl */ `
fn particleChaoticHash(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(127.1, 311.7, 74.7))) * 43758.5453123);
}

fn particleChaoticDirection(originalCenter: vec3f) -> vec3f {
  let effectScale = max(uniform.uEffectScale, 0.000001);
  let local = originalCenter / effectScale;
  let seedOffset = vec3f(
    uniform.uParticleSeed * 0.0131,
    uniform.uParticleSeed * 0.0173,
    uniform.uParticleSeed * 0.0199
  );
  let randomValue = vec3f(
    particleChaoticHash(local + seedOffset + vec3f(17.0, 3.0, 11.0)),
    particleChaoticHash(local.yzx + seedOffset + vec3f(7.0, 29.0, 5.0)),
    particleChaoticHash(local.zxy + seedOffset + vec3f(13.0, 19.0, 31.0))
  ) * 2.0 - 1.0;
  let lengthSquared = dot(randomValue, randomValue);
  if (lengthSquared < 0.000001) {
    return normalize(vec3f(0.57735, -0.57735, 0.57735));
  }
  return randomValue * inverseSqrt(lengthSquared);
}

fn particleLifecycleOffset(originalCenter: vec3f, scatterProgress: f32) -> vec3f {
  let speed = mix(
    0.55,
    1.45,
    particleChaoticHash(
      originalCenter / max(uniform.uEffectScale, 0.000001) + vec3f(uniform.uParticleSeed + 47.0)
    )
  );
  return particleChaoticDirection(originalCenter)
    * speed
    * scatterProgress
    * uniform.uScatterScale
    * uniform.uEffectScale;
}
`

/** 确定性无序粒子炸裂效果。 */
export class GSplatChaoticParticleScatterEffect extends GSplatParticleLifecycleEffect {
  constructor(options: GSplatChaoticParticleScatterEffectOptions = {}) {
    super({
      ...options,
      id: options.id ?? 'particle-scatter-chaotic',
    })
  }

  protected override getParticleMotionFieldGLSL(): string {
    return chaoticParticleMotionFieldGLSL
  }

  protected override getParticleMotionFieldWGSL(): string {
    return chaoticParticleMotionFieldWGSL
  }
}
